import { useEffect, useMemo, useState, Fragment } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  Bell, MessageCircle, Mail, CreditCard, Cog, Filter, RefreshCcw,
  CheckCircle2, XCircle, Clock, AlertTriangle, MinusCircle, ExternalLink,
  ChevronDown, ChevronRight, Search, X, Eye, ShieldAlert,
} from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { formatDateTime } from "@/lib/format";

const CHANNEL_META = {
  whatsapp: { label: "WhatsApp", icon: MessageCircle, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  email:    { label: "Email",    icon: Mail,          tone: "bg-blue-50 text-blue-700 border-blue-200" },
  stripe:   { label: "Stripe",   icon: CreditCard,    tone: "bg-purple-50 text-purple-700 border-purple-200" },
  system:   { label: "Sistema",  icon: Cog,           tone: "bg-zinc-100 text-zinc-700 border-zinc-200" },
};

const STATUS_META = {
  accepted: { label: "Accettato", icon: Clock, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  queued: { label: "In coda provider", icon: Clock, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  sending: { label: "Invio in corso", icon: Clock, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  delivered: { label: "Consegnato", icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  read: { label: "Letto", icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  undelivered: { label: "Non consegnato", icon: XCircle, tone: "bg-red-50 text-red-700 border-red-200" },
  unknown: { label: "Esito da verificare", icon: AlertTriangle, tone: "bg-amber-50 text-amber-700 border-amber-200" },
  sent:    { label: "Inviato",    icon: CheckCircle2, tone: "bg-emerald-50 text-emerald-700 border-emerald-200" },
  failed:  { label: "Fallito",    icon: XCircle,      tone: "bg-red-50 text-red-700 border-red-200" },
  pending: { label: "In coda",    icon: Clock,        tone: "bg-amber-50 text-amber-700 border-amber-200" },
  mocked:  { label: "Mocked",     icon: AlertTriangle, tone: "bg-amber-50 text-amber-800 border-amber-200" },
  skipped: { label: "Saltato",    icon: MinusCircle,  tone: "bg-zinc-100 text-zinc-600 border-zinc-200" },
};

const EVENT_LABEL = {
  order_created: "Ordine creato",
  order_accepted: "Ordine accettato",
  order_picking_up: "In ritiro",
  order_picked_up: "Ritirato",
  order_delivering: "In consegna",
  order_delivered: "Consegnato",
  order_cancelled: "Annullato",
  order_cancelled_admin: "Annullato (admin)",
  lead_hot: "Lead hot",
  lead_notification: "Notifica lead",
  referral_unlocked_driver: "Referral driver",
  referral_unlocked_referrer: "Referral inviter",
  admin_test: "Test admin",
};

function eventLabel(t) {
  return EVENT_LABEL[t] || (t || "").replace(/^stripe\./, "Stripe · ").replaceAll("_", " ");
}

function ChannelBadge({ value }) {
  const meta = CHANNEL_META[value] || CHANNEL_META.system;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${meta.tone}`}>
      <Icon className="size-3" />
      {meta.label}
    </span>
  );
}

function StatusBadge({ value }) {
  const meta = STATUS_META[value] || STATUS_META.pending;
  const Icon = meta.icon;
  return (
    <span className={`inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-[11px] font-semibold uppercase tracking-wide ${meta.tone}`}>
      <Icon className="size-3" />
      {meta.label}
    </span>
  );
}

function StatCard({ icon: Icon, label, value, tone, active, onClick, testId }) {
  return (
    <button
      type="button"
      onClick={onClick}
      data-testid={testId}
      className={`text-left rounded-2xl border p-4 transition-all hover:shadow-sm ${
        active ? "border-dapnow-blue ring-2 ring-dapnow-blue/20 bg-white" : "border-zinc-200 bg-white"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className={`inline-flex size-8 items-center justify-center rounded-lg ${tone}`}>
          <Icon className="size-4" />
        </span>
        {active && <span className="text-[10px] font-bold text-dapnow-blue uppercase">Attivo</span>}
      </div>
      <div className="mt-2 text-2xl font-extrabold text-dapnow-ink tabular-nums">{value}</div>
      <div className="text-[12px] text-dapnow-muted">{label}</div>
    </button>
  );
}

function preview(log) {
  if (log?.payload_preview) return log.payload_preview;
  const p = log?.payload || {};
  if (p.body) return p.body;
  if (p.subject) return p.subject;
  return JSON.stringify(p).slice(0, 240);
}

export default function AdminNotificationLogs() {
  const [data, setData] = useState({ items: [], overall: { by_channel: {}, by_status: {}, total: 0 }, total: 0 });
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [expanded, setExpanded] = useState(null);

  const [channel, setChannel] = useState("");
  const [status, setStatus] = useState("");
  const [orderId, setOrderId] = useState("");
  const [userId, setUserId] = useState("");
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [q, setQ] = useState("");
  const [fallbackOnly, setFallbackOnly] = useState(false);

  const queryParams = useMemo(() => {
    const p = { limit: 200 };
    if (channel) p.channel = channel;
    if (status) p.status = status;
    if (orderId.trim()) p.order_id = orderId.trim();
    if (userId.trim()) p.user_id = userId.trim();
    if (dateFrom) p.date_from = new Date(dateFrom).toISOString();
    if (dateTo) p.date_to = new Date(dateTo).toISOString();
    if (q.trim()) p.q = q.trim();
    if (fallbackOnly) p.fallback_only = true;
    return p;
  }, [channel, status, orderId, userId, dateFrom, dateTo, q, fallbackOnly]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    setRefreshing(true);
    try {
      const { data: resp } = await api.get("/admin/notification-logs", { params: queryParams });
      setData(resp);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channel, status, fallbackOnly]);

  const applyTextFilters = (e) => {
    e.preventDefault();
    load();
  };

  const clearAll = () => {
    setChannel(""); setStatus(""); setOrderId(""); setUserId("");
    setDateFrom(""); setDateTo(""); setQ(""); setFallbackOnly(false);
  };

  const overall = data.overall || { by_channel: {}, by_status: {}, total: 0 };
  const totalShown = data.total || 0;

  return (
    <div className="space-y-6" data-testid="admin-notifications-page">
      <header className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-extrabold tracking-tight text-dapnow-ink flex items-center gap-2">
            <Bell className="size-6 text-dapnow-blue" />
            Audit notifiche
          </h1>
          <p className="text-sm text-dapnow-muted mt-1 max-w-2xl">
            Tracciamento completo di tutte le comunicazioni inviate da DapNow (WhatsApp, email, eventi Stripe, sistema). Mostra anche gli invii in modalità mocked finché Twilio / Resend non sono live.
          </p>
        </div>
        <button
          data-testid="refresh-logs-btn"
          onClick={() => load({ silent: true })}
          className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-dapnow-ink hover:bg-zinc-50"
        >
          <RefreshCcw className={`size-4 ${refreshing ? "animate-spin" : ""}`} /> Aggiorna
        </button>
      </header>

      {/* Channel stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="channel-stats">
        <StatCard
          icon={Bell} label="Totali" value={overall.total} tone="bg-zinc-100 text-zinc-700"
          active={!channel} onClick={() => setChannel("")}
          testId="stat-all"
        />
        {["whatsapp", "email", "stripe", "system"].map((ch) => {
          const meta = CHANNEL_META[ch];
          return (
            <StatCard
              key={ch}
              icon={meta.icon} label={meta.label} value={overall.by_channel?.[ch] ?? 0}
              tone={meta.tone}
              active={channel === ch}
              onClick={() => setChannel(channel === ch ? "" : ch)}
              testId={`stat-${ch}`}
            />
          );
        })}
      </div>

      {/* Status pills */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="status-filter">
        <span className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mr-1">Stato:</span>
        <button
          onClick={() => setStatus("")}
          className={`inline-flex items-center gap-1 rounded-full border px-3 py-1 text-xs font-semibold transition ${
            !status ? "bg-dapnow-ink text-white border-dapnow-ink" : "bg-white border-zinc-200 text-dapnow-ink hover:bg-zinc-50"
          }`}
          data-testid="status-pill-all"
        >
          Tutti ({overall.total})
        </button>
        {Object.keys(STATUS_META).map((s) => {
          const m = STATUS_META[s];
          const Icon = m.icon;
          const c = overall.by_status?.[s] ?? 0;
          return (
            <button
              key={s}
              onClick={() => setStatus(status === s ? "" : s)}
              className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${
                status === s ? `${m.tone} ring-2 ring-offset-1 ring-dapnow-blue/20` : "bg-white border-zinc-200 text-dapnow-ink hover:bg-zinc-50"
              }`}
              data-testid={`status-pill-${s}`}
            >
              <Icon className="size-3" /> {m.label} ({c})
            </button>
          );
        })}
        <button
          onClick={() => setFallbackOnly((v) => !v)}
          className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs font-semibold transition ${
            fallbackOnly ? "bg-amber-100 text-amber-800 border-amber-300" : "bg-white border-zinc-200 text-dapnow-ink hover:bg-zinc-50"
          }`}
          data-testid="filter-fallback-toggle"
        >
          <ShieldAlert className="size-3" /> Solo fallback
        </button>
      </div>

      {/* Advanced filters */}
      <form
        onSubmit={applyTextFilters}
        className="rounded-2xl border border-zinc-200 bg-white p-4 grid md:grid-cols-12 gap-3 items-end"
        data-testid="advanced-filters"
      >
        <div className="md:col-span-4">
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">Cerca</label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-dapnow-muted" />
            <input
              data-testid="filter-q"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Telefono, email, evento, errore…"
              className="w-full rounded-xl border border-zinc-200 pl-9 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/30"
            />
          </div>
        </div>
        <div className="md:col-span-3">
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">Order ID</label>
          <input
            data-testid="filter-order-id"
            value={orderId}
            onChange={(e) => setOrderId(e.target.value)}
            placeholder="UUID ordine"
            className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/30"
          />
        </div>
        <div className="md:col-span-3">
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">User ID</label>
          <input
            data-testid="filter-user-id"
            value={userId}
            onChange={(e) => setUserId(e.target.value)}
            placeholder="UUID utente"
            className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/30"
          />
        </div>
        <div className="md:col-span-2 flex flex-col">
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">Da</label>
          <input
            data-testid="filter-date-from"
            type="datetime-local"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/30"
          />
        </div>
        <div className="md:col-span-2">
          <label className="block text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">A</label>
          <input
            data-testid="filter-date-to"
            type="datetime-local"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            className="w-full rounded-xl border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-dapnow-blue/30"
          />
        </div>
        <div className="md:col-span-8 flex items-center gap-2">
          <button
            type="submit"
            className="inline-flex items-center gap-2 rounded-xl bg-dapnow-blue px-4 py-2 text-sm font-semibold text-white hover:bg-dapnow-blue/90"
            data-testid="apply-filters-btn"
          >
            <Filter className="size-4" /> Applica
          </button>
          <button
            type="button"
            onClick={clearAll}
            className="inline-flex items-center gap-2 rounded-xl border border-zinc-200 bg-white px-3 py-2 text-sm font-semibold text-dapnow-ink hover:bg-zinc-50"
            data-testid="clear-filters-btn"
          >
            <X className="size-4" /> Pulisci filtri
          </button>
          <span className="ml-auto text-xs text-dapnow-muted">
            <strong className="text-dapnow-ink">{totalShown}</strong> risultati
          </span>
        </div>
      </form>

      {/* Table */}
      <div className="rounded-2xl border border-zinc-200 bg-white overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-dapnow-muted">Caricamento…</div>
        ) : data.items.length === 0 ? (
          <div className="p-10 text-center text-dapnow-muted">
            Nessuna notifica trovata con questi filtri.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ minWidth: "1000px" }} data-testid="notif-logs-table">
              <thead className="bg-zinc-50 text-[11px] uppercase tracking-wide text-dapnow-muted">
                <tr>
                  <th className="px-3 py-2 text-left w-8"></th>
                  <th className="px-3 py-2 text-left">Quando</th>
                  <th className="px-3 py-2 text-left">Canale</th>
                  <th className="px-3 py-2 text-left">Evento</th>
                  <th className="px-3 py-2 text-left">Stato</th>
                  <th className="px-3 py-2 text-left">Destinatario</th>
                  <th className="px-3 py-2 text-left">Collegato a</th>
                  <th className="px-3 py-2 text-left">Anteprima</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-zinc-100">
                {data.items.map((log, idx) => {
                  const key = log.id || `${log.created_at}-${idx}`;
                  const isOpen = expanded === key;
                  return (
                    <Fragment key={key}>
                      <tr
                        className={`hover:bg-zinc-50 cursor-pointer ${log.error_message ? "bg-red-50/30" : ""}`}
                        onClick={() => setExpanded(isOpen ? null : key)}
                        data-testid={`notif-log-row-${idx}`}
                      >
                        <td className="px-3 py-3">
                          {isOpen ? <ChevronDown className="size-4 text-dapnow-muted" /> : <ChevronRight className="size-4 text-dapnow-muted" />}
                        </td>
                        <td className="px-3 py-3 whitespace-nowrap text-xs text-dapnow-muted">
                          {formatDateTime(log.created_at)}
                        </td>
                        <td className="px-3 py-3"><ChannelBadge value={log.channel} /></td>
                        <td className="px-3 py-3 font-semibold text-dapnow-ink">{eventLabel(log.notification_type)}</td>
                        <td className="px-3 py-3">
                          <div className="flex items-center gap-1.5">
                            <StatusBadge value={log.status} />
                            {log.fallback_used && (
                              <span className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 text-amber-800 px-1.5 py-0.5 text-[10px] font-bold uppercase">
                                <ShieldAlert className="size-2.5" /> Fallback
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="px-3 py-3 font-mono text-xs text-dapnow-ink truncate max-w-[180px]">
                          {log.recipient || "—"}
                        </td>
                        <td className="px-3 py-3 text-xs">
                          {log.related_order_id && (
                            <Link
                              to="/admin/orders"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-dapnow-blue hover:underline font-mono"
                              data-testid={`notif-link-order-${idx}`}
                            >
                              #{log.related_order_id.slice(-6).toUpperCase()} <ExternalLink className="size-3" />
                            </Link>
                          )}
                          {log.related_user_id && (
                            <Link
                              to="/admin/users"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-dapnow-blue hover:underline font-mono"
                              data-testid={`notif-link-user-${idx}`}
                            >
                              {log.related_user_id.slice(0, 6)}… <ExternalLink className="size-3" />
                            </Link>
                          )}
                          {log.related_lead_id && (
                            <Link
                              to="/admin/leads"
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1 text-dapnow-blue hover:underline font-mono"
                              data-testid={`notif-link-lead-${idx}`}
                            >
                              Lead <ExternalLink className="size-3" />
                            </Link>
                          )}
                          {!log.related_order_id && !log.related_user_id && !log.related_lead_id && (
                            <span className="text-dapnow-muted">—</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-xs text-dapnow-muted truncate max-w-[260px]">
                          {preview(log)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="bg-zinc-50/60">
                          <td colSpan={8} className="px-6 py-4">
                            <div className="grid md:grid-cols-2 gap-4" data-testid={`notif-log-detail-${idx}`}>
                              <div>
                                <div className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-2 flex items-center gap-1">
                                  <Eye className="size-3" /> Payload completo
                                </div>
                                <pre className="rounded-lg border border-zinc-200 bg-white p-3 text-xs text-dapnow-ink overflow-x-auto max-h-72 whitespace-pre-wrap break-words">
{JSON.stringify(log.payload || {}, null, 2)}
                                </pre>
                              </div>
                              <div className="space-y-3">
                                {log.error_message && (
                                  <div>
                                    <div className="text-[11px] uppercase tracking-wide font-semibold text-red-700 mb-1 flex items-center gap-1">
                                      <XCircle className="size-3" /> Errore
                                    </div>
                                    <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-xs text-red-800 font-mono break-words">
                                      {log.error_message}
                                    </div>
                                  </div>
                                )}
                                {log.provider_response && (
                                  <div>
                                    <div className="text-[11px] uppercase tracking-wide font-semibold text-dapnow-muted mb-1">Risposta provider</div>
                                    <pre className="rounded-lg border border-zinc-200 bg-white p-3 text-xs text-dapnow-ink overflow-x-auto max-h-40 whitespace-pre-wrap break-words">
{JSON.stringify(log.provider_response, null, 2)}
                                    </pre>
                                  </div>
                                )}
                                <div className="grid grid-cols-2 gap-2 text-xs">
                                  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
                                    <div className="text-[10px] uppercase text-dapnow-muted">ID log</div>
                                    <div className="font-mono text-dapnow-ink truncate">{log.id || "—"}</div>
                                  </div>
                                  <div className="rounded-lg border border-zinc-200 bg-white px-3 py-2">
                                    <div className="text-[10px] uppercase text-dapnow-muted">Provider SID</div>
                                    <div className="font-mono text-dapnow-ink truncate">{log.provider_sid || "—"}</div>
                                  </div>
                                </div>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
